<template>
    <EditComponent
        dialog-id="editHeaderDialog"
        open-dialog-event="header:edit-mode"
        updated-event="header:updated"
        :confirm-function="update"
        confirm-data-testid="profile-edition-section-confirm-button"
        :open-dialog-post-function="openDialogPostOpen"
        :track-section-opened="trackingOpenedSection"
        :track-existing-element="true"
        size="default"
        success-message-key="profile.section.header.edit.success"
        @close="resetValues"
    >
        <template #edit-header>
            <p>{{ $t('profile.section.header.edit.title') }}</p>
        </template>
        <template #edit-body>
            <VJoyWrapper direction="column" align="stretch" row-gap="8">
                <EditHeadline v-model="state.headline" :errors="r$.headline.$errors" data-testid="edit-header-headline" />
                <EditLevelOfExperience v-model="state.experienceLevel" data-testid="edit-header-experience-level" />
                <EditPrice
                    v-model:price="state.price"
                    v-model:price-hidden="state.priceHidden"
                    :errors="r$.price.$errors"
                    :current-currency="currentCurrency"
                    data-testid="edit-header-price"
                />
                <EditTopSkills
                    id="edit-header-top-skills"
                    v-model="state.topSkills"
                    :headline="state.headline"
                    :errors="r$.topSkills.$errors?.$self ?? r$.topSkills.$errors"
                    data-testid="edit-header-top-skills"
                />
                <EditLocation v-model="state.address" :errors="r$.address.formattedAddress.$errors" data-testid="edit-header-location" />
                <EditDisplayOption
                    v-model="state.nameDisplayOption"
                    :errors="r$.nameDisplayOption.$errors"
                    data-testid="edit-header-display-option"
                />
            </VJoyWrapper>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {ref, onMounted, computed} from 'vue';
    import {required, minLength, maxLength, withMessage, minValue, maxValue} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {useRoute, useTranslation, useLocale} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {usePriceFormatterNoFraction} from '~/composables/usePriceFormatter';
    import {eventBus} from '~/config/events';
    import {EditionOpenedSection, EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {NameDisplayOption} from '~/openapi';
    import {useProfileStore} from '~/store/profileStore';
    import {useRulesStore} from '~/store/rules/rulesStore';
    import {useSkillSetStore} from '~/store/skill-set/skillSetStore';
    import {ExperienceLevel} from '~/types/domain/Profile';
    import EditDisplayOption from './EditDisplayOption.vue';
    import EditHeadline from './EditHeadline.vue';
    import EditLevelOfExperience from './EditLevelOfExperience.vue';
    import EditLocation from './EditLocation.vue';
    import EditPrice from './EditPrice.vue';
    import EditTopSkills from './EditTopSkills.vue';
    import {useProfileHeader} from '../../composable/useProfileHeader';

    const {t} = useTranslation();
    const trackingOpenedSection = EditionOpenedSection.HEADER;

    const profileStore = useProfileStore();
    const {getAveragePriceCurrentFreelancer} = profileStore;
    const {profile} = storeToRefs(profileStore);
    const {profileTopSkills} = storeToRefs(useSkillSetStore());
    const defaultState = computed(() => ({
        headline: profile.value?.headline ?? '',
        experienceLevel: profile.value?.experienceLevel ?? ExperienceLevel.ENTRY,
        price: profile.value?.price.price ?? 0,
        priceHidden: profile.value?.price.priceHidden ?? false,
        topSkills: profileTopSkills.value ?? [],
        address: profile.value?.locationPreferences.address ?? {formattedAddress: ''},
        nameDisplayOption: (profile.value?.privacy.nameDisplayOption ?? NameDisplayOption.Full) as NameDisplayOption,
    }));
    const state = ref({...defaultState.value});

    const {locale} = useLocale();
    const priceFormatted = computed(() => usePriceFormatterNoFraction(profile.value!.price.currencyCode, locale.value));
    const currentCurrency = computed(() => priceFormatted.value.resolvedOptions().currency!);
    const minPriceFormatted = computed(() => priceFormatted.value.format(profile.value!.price.minPrice));

    const {skillSetTopSkills, jobTitle, dailyRatePrice} = storeToRefs(useRulesStore());

    const rules = computed(() => ({
        headline: {required, minLength: minLength(jobTitle.value.min), maxLength: maxLength(jobTitle.value.max)},
        experienceLevel: {required},
        price: {
            required,
            minValue: withMessage(minValue(profile.value!.price.minPrice), t('profile.wizard.price.minimum.details.error', {0: minPriceFormatted})),
            maxValue: maxValue(dailyRatePrice.value.max),
        },
        priceHidden: {required},
        topSkills: {
            minLength: withMessage(
                minLength(skillSetTopSkills.value.min),
                t('profile.section.header.edit.topskills.error.min', {minSkills: skillSetTopSkills.value.min}),
            ),
            maxLength: withMessage(
                maxLength(skillSetTopSkills.value.max),
                t('profile.section.header.edit.topskills.error.max', {maxSkills: skillSetTopSkills.value.max}),
            ),
        },
        address: {formattedAddress: {required}},
        nameDisplayOption: {required},
    }));
    const {r$} = useRegle(state, rules);

    const {updateProfileHeader} = useProfileHeader();
    async function update() {
        const {valid} = await validateForm(r$);
        if (!valid) {
            return false;
        }

        await updateProfileHeader({
            dailyRate: {
                currencyCode: currentCurrency.value,
                price: state.value.price,
                hidden: state.value.priceHidden,
            },
            experienceLevel: state.value.experienceLevel,
            headline: state.value.headline,
            location: state.value.address,
            nameDisplayOption: state.value.nameDisplayOption,
            topSkills: state.value.topSkills,
        });
    }

    const route = useRoute();
    onMounted(() => {
        if ('edit-experience-level' in route.query) {
            eventBus.emit('header:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
        }
    });

    async function openDialogPostOpen() {
        await getAveragePriceCurrentFreelancer();
    }

    function resetValues() {
        state.value = {...defaultState.value};
        r$.$reset();
    }
</script>
